/* What needs a person, as the reference's notification cards rather than a table.
 *
 * **The same merged worklist the old table drew, ordered by the same model.** Incidents, anomalies,
 * the PII review queue and remediation approvals are merged and ranked in `orderAttention`, so the
 * front page and its tests still agree about what is worst. What changed is the form: a worklist of
 * decisions reads as a short stack of cards — what, why, how long, and the one action — and a table
 * with column headers was the densest thing on a page meant to be glanced at.
 *
 * **Severity is a shape and a word as well as a hue.** A critical card carries an octagon and the word
 * "Critical", a warning a triangle and "Warning", so the stack survives greyscale, a projector and
 * every form of colour-blindness — the rule every status in this product follows.
 *
 * **Four first, the rest one click away, and the count says how many there are.** Progressive
 * disclosure rather than a cut: "Show all 9" expands in place, so nothing the platform found is
 * unreachable from the page that found it.
 *
 * **A list that could not read one of its sources says so above the cards.** A failed read makes the
 * stack *shorter* while looking complete, which reads as "less needs me" — the inversion of what
 * happened. `blind` names the sources that did not answer.
 */

import { useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useReveal } from '@/shared/ui/motion';

import type { AttentionItem } from '../model/overview';
import {
  AlertIcon,
  ArrowIcon,
  OctagonIcon,
  PulseIcon,
  ShieldIcon,
  StampIcon,
} from './icons';
import { Panel, Skeleton } from './OverviewParts';

/** How many cards before "Show all". Four is what fits beside the trend without the rail outgrowing
 * the main column on a laptop screen. */
export const ATTENTION_PREVIEW = 4;

const TONE_CLASS = {
  danger: 'lq-ov-attn__item lq-ov-attn__item--danger',
  warn: 'lq-ov-attn__item lq-ov-attn__item--warn',
  neutral: 'lq-ov-attn__item lq-ov-attn__item--neutral',
} as const satisfies Record<AttentionItem['tone'], string>;

/** The word on the card's tag: what kind of thing is waiting, in the severity's terms where it has
 * one. An incident says its severity; the queues say what they are. */
function tagKey(item: AttentionItem): string {
  if (item.kind === 'incident') {
    return item.tone === 'danger'
      ? 'home.attention.tag.critical'
      : 'home.attention.tag.warning';
  }
  return `home.attention.tag.${item.kind}`;
}

function TagIcon({ item }: { readonly item: AttentionItem }): ReactElement {
  if (item.kind === 'incident') {
    return item.tone === 'danger' ? (
      <OctagonIcon className="lq-ov-icon" />
    ) : (
      <AlertIcon className="lq-ov-icon" />
    );
  }
  if (item.kind === 'anomaly') return <PulseIcon className="lq-ov-icon" />;
  if (item.kind === 'review') return <ShieldIcon className="lq-ov-icon" />;
  return <StampIcon className="lq-ov-icon" />;
}

export function AttentionPanel({
  items,
  targets,
  blind,
  settled,
  unknownAge,
}: {
  readonly items: readonly AttentionItem[];
  readonly targets: Readonly<Record<AttentionItem['kind'], string>>;
  readonly blind: readonly string[];
  readonly settled: boolean;
  /** The age string that means "no age is known" — rendered as nothing rather than as a dash. */
  readonly unknownAge: string;
}): ReactElement {
  const { t } = useTranslation();
  const [all, setAll] = useState(false);
  const shown = all ? items : items.slice(0, ATTENTION_PREVIEW);

  const list = useReveal<HTMLUListElement>(
    settled && items.length > 0,
    (root, timeline) => {
      timeline.from(root.querySelectorAll('.lq-ov-attn__item'), {
        y: 8,
        opacity: 0,
        duration: 0.4,
        stagger: 0.06,
      });
    },
    'rail',
  );

  return (
    <Panel
      title={t('home.attention.title')}
      subtitle={
        settled && items.length > 0
          ? t('home.attention.count', { count: items.length })
          : undefined
      }
      busy={!settled}
      className="lq-ov-attn"
    >
      {blind.length > 0 ? (
        <p className="lq-ov-attn__blind" role="note">
          {t('home.attention.blind', { sources: blind.join(', ') })}
        </p>
      ) : null}

      {!settled ? (
        <Skeleton
          label={t('home.attention.loading')}
          lines={['long', 'medium', 'long', 'medium']}
        />
      ) : items.length === 0 ? (
        /* "Every source read cleanly" is only true when every source did. With one unreadable, the
           note above already says the list is incomplete, and this line must not contradict it. */
        <p className="lq-ov-note">
          {blind.length > 0
            ? t('home.attention.clearPartial')
            : t('home.attention.clearGuidance')}
        </p>
      ) : (
        <>
          <ul ref={list} className="lq-ov-attn__list">
            {shown.map((item) => (
              <li key={item.id} className={TONE_CLASS[item.tone]}>
                <span className="lq-ov-attn__icon">
                  <TagIcon item={item} />
                </span>
                <div className="lq-ov-attn__body">
                  <div className="lq-ov-attn__top">
                    <p
                      className={
                        item.named
                          ? 'lq-ov-attn__subject'
                          : 'lq-ov-attn__subject lq-ov-attn__subject--unnamed'
                      }
                    >
                      {item.subject}
                    </p>
                    <span className="lq-ov-attn__tag">{t(tagKey(item))}</span>
                  </div>
                  {/* Clamped to two lines on the card; the whole sentence is on `title`, and on
                      the screen the card opens. */}
                  <p className="lq-ov-attn__why" title={item.why}>
                    {item.why}
                  </p>
                  <div className="lq-ov-attn__meta">
                    {item.since === unknownAge ? (
                      <span />
                    ) : (
                      <span className="lq-ov-attn__since">
                        {t('home.attention.openFor', { age: item.since })}
                      </span>
                    )}
                    <Link className="lq-ov-attn__act" to={targets[item.kind]}>
                      {t(`home.attention.act.${item.kind}`)}
                      <ArrowIcon className="lq-ov-icon lq-ov-icon--arrow" />
                    </Link>
                  </div>
                </div>
              </li>
            ))}
          </ul>
          {items.length > ATTENTION_PREVIEW ? (
            <button
              type="button"
              className="lq-ov-more"
              aria-expanded={all}
              onClick={() => {
                setAll(!all);
              }}
            >
              {all
                ? t('home.attention.showFewer')
                : t('home.attention.showAll', { count: items.length })}
            </button>
          ) : null}
        </>
      )}
    </Panel>
  );
}
